import {
    Box,
    Card,
    CardContent,
    CircularProgress,
    List,
    Stack,
    Typography,
    Alert,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
    useMedicalActRecords,
    MedicalActsSectionHeader,
    MedicalActsTotalFooter,
    MedicalActRecordItem,
    MedicalActRecordCompactItem,
} from '@/features/medical-acts'
import { RecordMedicalActModal } from '@/features/medical-acts/ui/RecordMedicalActModal'

export type MedicalActsSectionProps = {
    patientId: string
    consultationId?: string
    expertiseId?: string
    readOnly?: boolean
    showTitle?: boolean
    compact?: boolean
}

export function MedicalActsSection({
    patientId,
    consultationId,
    expertiseId,
    readOnly = false,
    showTitle = true,
    compact = false,
}: MedicalActsSectionProps) {
    const { t } = useTranslation()
    const {
        records,
        isLoading,
        isDeleting,
        isModalOpen,
        totalRevenue,
        handleRefetch,
        handleOpenModal,
        handleCloseModal,
        handleDelete,
    } = useMedicalActRecords({ patientId, consultationId, expertiseId })

    const renderContent = () => {
        if (isLoading) {
            return (
                <Box display="flex" justifyContent="center" p={compact ? 2 : 4}>
                    <CircularProgress size={compact ? 24 : 40} />
                </Box>
            )
        }

        if (!records || records.length === 0) {
            return compact ? (
                <Typography variant="body2" color="text.secondary">
                    {t('medicalAct.section.empty', { defaultValue: 'No medical acts recorded' })}
                </Typography>
            ) : (
                <Alert severity="info">
                    {t('medicalAct.section.empty', { defaultValue: 'No medical acts recorded yet' })}
                </Alert>
            )
        }

        if (compact) {
            return (
                <Stack spacing={0.5}>
                    {records.map((record) => (
                        <MedicalActRecordCompactItem key={record._id} record={record} />
                    ))}
                    <MedicalActsTotalFooter totalRevenue={totalRevenue} compact />
                </Stack>
            )
        }

        return (
            <>
                <List disablePadding>
                    {records.map((record, index) => (
                        <MedicalActRecordItem
                            key={record._id}
                            record={record}
                            index={index}
                            readOnly={readOnly}
                            isDeleting={isDeleting}
                            onDelete={handleDelete}
                        />
                    ))}
                </List>
                <MedicalActsTotalFooter totalRevenue={totalRevenue} />
            </>
        )
    }

    const modal = (
        <RecordMedicalActModal
            open={isModalOpen}
            onClose={handleCloseModal}
            patientId={patientId}
            consultationId={consultationId}
            expertiseId={expertiseId}
            onSuccess={handleRefetch}
        />
    )

    if (compact) {
        return (
            <>
                <Stack spacing={1}>
                    <MedicalActsSectionHeader
                        count={records?.length}
                        readOnly={readOnly}
                        compact
                        onAddClick={handleOpenModal}
                    />
                    {renderContent()}
                </Stack>
                {modal}
            </>
        )
    }

    return (
        <>
            <Card variant="outlined">
                <CardContent>
                    <Stack spacing={2}>
                        {showTitle && (
                            <MedicalActsSectionHeader
                                count={records?.length}
                                readOnly={readOnly}
                                showTitle={showTitle}
                                onAddClick={handleOpenModal}
                            />
                        )}
                        {renderContent()}
                    </Stack>
                </CardContent>
            </Card>
            {modal}
        </>
    )
}

export default MedicalActsSection
